import { useState } from 'react';
import { createDocument, listDocuments, updateDocument } from '../lifeos/api.js';
import { daysBetween, humanize, shortDate, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { options, text, toValues } from '../lifeos/forms.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, EmptyState, Group, IconButton, Loader, Page, Row, Meta, useParamSetter, useSphereLens, When } from '../ui/index.js';
import type { DocumentKind, LifeDocument } from '../lifeos/types.js';

const KINDS: DocumentKind[] = [
  'passport',
  'drivers_license',
  'id_card',
  'insurance',
  'payment_card',
  'registration',
  'warranty',
  'receipt',
  'contract',
  'certificate',
  'other',
];

/** Days ahead from which an expiry turns amber: renewing a passport or licence takes weeks. */
export const RENEW_WINDOW_DAYS = 180;

/** Expiry chip: red once expired, amber inside six months, quiet after that. */
export function expiryTone(expiresOn: string | null, today: string): { label: string; tone: 'overdue' | 'renew' | 'later' } | null {
  if (!expiresOn) return null;
  const n = daysBetween(today, expiresOn);
  if (n < 0) return { label: `expired ${shortDate(expiresOn, today)}`, tone: 'overdue' };
  return { label: `expires ${shortDate(expiresOn, today)}`, tone: n <= RENEW_WINDOW_DAYS ? 'renew' : 'later' };
}

/** Last-4 rule (§3.4): at most four letters or digits, never a full number. */
export const LAST4 = /^[0-9A-Za-z]{0,4}$/;

/**
 * Documents (`/documents`): IDs, cards and policies with their expiry dates.
 * Only the last four characters of a number are ever stored; expiry
 * reminders come from the derived obligation.
 */
export default function Documents(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const list = useLoad(() => listDocuments({ sphere: sphere ?? undefined }), [sphere]);
  const [editing, setEditing] = useState<LifeDocument | 'new' | null>(null);
  const today = ymdInHomeZone(new Date());
  const soon = list.data?.filter((d) => {
    const t = expiryTone(d.expiresOn, today)?.tone;
    return t === 'overdue' || t === 'renew';
  }).length;

  return (
    <Page
      title="Documents"
      sub={soon ? `${soon} to renew soon` : undefined}
      actions={
        <>
          {lens.button}
          <IconButton icon="plus" label="Add" onClick={() => setEditing('new')} />
        </>
      }
    >
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Loader state={list}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState
              icon="file"
              title="No documents yet."
              hint="Add a passport or card to get reminded before it expires."
              action={
                <Button variant="secondary" icon="plus" onClick={() => setEditing('new')}>
                  Add one
                </Button>
              }
            />
          ) : (
            <Group label="Documents">
              {[...items]
                .sort((a, b) => (a.expiresOn ?? '9999').localeCompare(b.expiresOn ?? '9999'))
                .map((d) => {
                  const exp = expiryTone(d.expiresOn, today);
                  return (
                    <Row
                      key={d.id}
                      onClick={() => setEditing(d)}
                      ariaLabel={`Edit ${d.label}`}
                      title={d.label}
                      sub={
                        <>
                          {exp && <When tone={exp.tone}>{exp.label}</When>}
                          <Meta color={sphereColor(spheres, d.sphereKey)}>
                            {humanize(d.kind)}
                            {d.holder && ` · ${d.holder}`}
                          </Meta>
                        </>
                      }
                      detail={d.numberLast4 ? <span className="num">···· {d.numberLast4}</span> : undefined}
                      chevron={false}
                    />
                  );
                })}
            </Group>
          )
        }
      </Loader>
      {lens.sheet}
      {editing && (
        <FormSheet
          title={editing === 'new' ? 'Add a document' : `Edit ${editing.label}`}
          submitLabel={editing === 'new' ? 'Add' : 'Save'}
          fields={[
            { key: 'label', label: 'Name', required: true, wide: true, placeholder: 'e.g. Chase Sapphire' },
            { key: 'kind', label: 'Kind', kind: 'select', options: options(KINDS, humanize) },
            { key: 'holder', label: 'Holder' },
            { key: 'issuer', label: 'Issuer' },
            { key: 'numberLast4', label: 'Last 4', maxLength: 4, pattern: '[0-9A-Za-z]{0,4}', hint: 'Never the full number.' },
            { key: 'issuedOn', label: 'Issued', kind: 'date' },
            { key: 'expiresOn', label: 'Expires', kind: 'date' },
            { key: 'perks', label: 'Perks', wide: true, placeholder: 'e.g. lounge access, rental car insurance' },
            { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
            { key: 'notes', label: 'Notes', kind: 'textarea' },
          ]}
          initial={editing === 'new' ? { kind: 'passport', sphereKey: 'personal' } : toValues({ ...editing })}
          onClose={() => setEditing(null)}
          onSubmit={async (v) => {
            const last4 = text(v.numberLast4);
            if (last4 && !LAST4.test(last4)) throw new Error('Last 4: at most four letters or digits.');
            const body = {
              label: text(v.label)!,
              kind: v.kind as DocumentKind,
              holder: text(v.holder),
              issuer: text(v.issuer),
              numberLast4: last4,
              issuedOn: text(v.issuedOn),
              expiresOn: text(v.expiresOn),
              perks: text(v.perks),
              sphereKey: v.sphereKey || undefined,
              notes: text(v.notes),
            };
            if (editing === 'new') await createDocument(body);
            else await updateDocument(editing.id, body);
            void list.reload();
          }}
        />
      )}
    </Page>
  );
}
